"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import Image from "next/image";
import { ImagePlus, X, Loader2 } from "lucide-react";
import { uploadErrorNoteAttachments } from "../lib/error-note-attachment-upload";

interface ErrorNoteImageUploadProps {
  imageUrls: string[];
  onImageUrlsChange: (urls: string[]) => void;
  maxImages?: number;
  isUploading: boolean;
  onUploadingChange: (uploading: boolean) => void;
  /**
   * 신규 첨부를 막는다(추가 첨부 영역 숨김). 이미 첨부한 이미지는 그대로(fallback) 표시한다.
   * 오답노트 복습 패널(/my/error-notes)에서 이미지 무한 추가를 방지하기 위해 켠다 (JCG-2156).
   */
  disableUpload?: boolean;
}

export default function ErrorNoteImageUpload({
  imageUrls,
  onImageUrlsChange,
  maxImages = 3,
  isUploading,
  onUploadingChange,
  disableUpload = false,
}: ErrorNoteImageUploadProps) {
  const uploadInProgressRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const [localPreviews, setLocalPreviews] = useState<{ file: File; preview: string }[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 컴포넌트 언마운트 시 미리보기 URL 정리
  useEffect(() => {
    return () => {
      localPreviews.forEach((item) => URL.revokeObjectURL(item.preview));
    };
  }, [localPreviews]);

  // 파일 검증
  const validateFile = (file: File): string | null => {
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      return "허용되지 않는 파일 형식입니다.";
    }
    if (file.size > maxSize) {
      return "파일 크기는 5MB 이하여야 합니다.";
    }
    return null;
  };

  // 파일 업로드
  const uploadFile = async (file: File): Promise<string | null> => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch("/api/upload-error-note-image", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();
      if (data.success && data.url) {
        return data.url;
      }
      console.error("Upload failed:", data.error);
      return null;
    } catch (error) {
      console.error("Upload error:", error);
      return null;
    }
  };

  // 파일 추가 처리
  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || disableUpload || isUploading || uploadInProgressRef.current) return;

      const currentCount = imageUrls.length + localPreviews.length;
      const validFiles: { file: File; preview: string }[] = [];

      for (let i = 0; i < files.length && currentCount + validFiles.length < maxImages; i++) {
        const file = files[i];
        const error = validateFile(file);

        if (error) {
          alert(error);
          continue;
        }

        validFiles.push({
          file,
          preview: URL.createObjectURL(file),
        });
      }

      if (currentCount + validFiles.length >= maxImages && files.length > validFiles.length) {
        alert(`이미지는 최대 ${maxImages}개까지 첨부할 수 있습니다.`);
      }

      if (validFiles.length === 0) return;

      // 미리보기 추가
      uploadInProgressRef.current = true;
      setLocalPreviews((prev) => [...prev, ...validFiles]);
      onUploadingChange(true);

      const uploadedUrls = await uploadErrorNoteAttachments(
        validFiles.map((item) => item.file),
        uploadFile,
      );
      uploadInProgressRef.current = false;
      onUploadingChange(false);

      setLocalPreviews([]);
      if (uploadedUrls.length > 0) {
        onImageUrlsChange([...imageUrls, ...uploadedUrls]);
      }
    },
    [imageUrls, localPreviews.length, maxImages, onImageUrlsChange, onUploadingChange, disableUpload, isUploading]
  );

  // 드래그 앤 드롭 핸들러
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (isUploading) return;
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  // 파일 선택 핸들러
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // 이미지 제거
  const handleRemove = (index: number) => {
    const newUrls = imageUrls.filter((_, i) => i !== index);
    onImageUrlsChange(newUrls);
  };

  const totalCount = imageUrls.length + localPreviews.length;

  return (
    <div className="space-y-3">
      {/* 이미지 미리보기 */}
      {(imageUrls.length > 0 || localPreviews.length > 0) && (
        <div className="flex flex-wrap gap-2">
          {/* 저장된 이미지 */}
          {imageUrls.map((url, index) => (
            <div key={`saved-${index}`} className="relative group">
              <a href={url} target="_blank" rel="noopener noreferrer">
                <Image
                  unoptimized={url.startsWith("/api/error-note-image?")}
                  src={url}
                  alt={`첨부 이미지 ${index + 1}`}
                  width={80}
                  height={80}
                  className="w-20 h-20 object-cover rounded-md border border-gray-200 dark:border-neutral-500 cursor-pointer hover:opacity-80 transition-opacity"
                />
              </a>
              <button
                type="button"
                onClick={() => handleRemove(index)}
                aria-label={`첨부 이미지 ${index + 1} 제거`}
                className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {/* 업로드 중인 이미지 */}
          {localPreviews.map((item, index) => (
            <div key={`preview-${index}`} className="relative">
              <a href={item.preview} download={item.file.name} aria-label={`첨부 이미지 ${index + 1} 원본 다운로드`}>
                <Image
                  src={item.preview}
                  alt={`저장 대기 이미지 ${index + 1}`}
                  width={80}
                  height={80}
                  className="w-20 h-20 object-cover rounded-md border border-gray-200 dark:border-neutral-500 opacity-50"
                />
              </a>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
              </div>
            </div>
          ))}
        </div>
      )}
      {/* 드래그 앤 드롭 영역 — 복습 패널(disableUpload)에서는 숨겨 추가 첨부를 막는다 */}
      {!disableUpload && totalCount < maxImages && (
        <div
          role="button"
          tabIndex={isUploading ? -1 : 0}
          aria-disabled={isUploading}
          onKeyDown={(e) => {
            if (!isUploading && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => { if (!isUploading) fileInputRef.current?.click(); }}
          className={`border-2 border-dashed rounded-md p-3 text-center cursor-pointer transition-colors focus-visible:outline-2 focus-visible:outline-ring ${
            isDragging
              ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
              : "border-gray-300 dark:border-neutral-500 hover:border-blue-400 dark:hover:border-blue-500"
          } ${isUploading ? "cursor-not-allowed opacity-50" : ""}`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            multiple
            aria-label="오답노트 첨부 이미지 선택"
            onChange={handleFileSelect}
            className="hidden"
            disabled={isUploading}
          />
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-neutral-400">
            <ImagePlus className="w-4 h-4" />
            <span>
              {isUploading ? "업로드 중..." : "이미지 첨부"}
            </span>
            <span className="text-xs">
              ({totalCount}/{maxImages})
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
